import { trpc } from '@/utils/trpc';
import { useQuery } from '@tanstack/react-query';
import { SourceType } from '@/feature/patients/types';

export type MedicalDataSortColumn =
  | 'sourceType'
  | 'dateUploaded'
  | 'dateOfStudy'
  | 'dateAdministered'
  | 'name'
  | 'id';
export type SortOrder = 'asc' | 'desc';

export interface MedicalDataSortColumnDef {
  column: MedicalDataSortColumn;
  order?: SortOrder;
}

export interface MedicalDataPagination {
  offset: number;
  limit: number;
}

export interface MedicalDataQueryParams {
  patientId: string;
  pagination?: MedicalDataPagination;
  sortColumns?: MedicalDataSortColumnDef[];
}

export const useGetPatientMedicalDataCounts = (patientId?: string) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientMedicalDataCounts.queryOptions({
      patientId: patientId || '',
    }),
    enabled: !!patientId,
    meta: { errorMessage: 'Failed to fetch medical data counts' },
  });

export const useGetPatientMedicalDataProblemsSummaries = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientProblemsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Problems' },
  });

export interface MedicalDataProblemsDetailsParams {
  patientId?: string;
  sourceType?: SourceType;
  dataRecordId?: string;
}

export const useGetPatientMedicalDataProblemsDetails = (
  params: MedicalDataProblemsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientProblemsDetails.queryOptions({
      patientId: params.patientId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      dataRecordId: params.dataRecordId,
    }),
    enabled: !!params.patientId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Problem Details' },
  });

export const useGetPatientMedicalDataMedicationsSummaries = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientMedicationsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Medications' },
  });

export const useGetPatientMedicalDataAllMedicationData = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientAllMedicationData.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch All Medication Data' },
  });

export interface MedicalDataMedicationsDetailsParams {
  patientId?: string;
  sourceType?: SourceType;
  dataRecordId?: string;
}

export const useGetPatientMedicalDataMedicationsDetails = (
  params: MedicalDataMedicationsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientMedicationsDetails.queryOptions({
      patientId: params.patientId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      dataRecordId: params.dataRecordId,
    }),
    enabled: !!params.patientId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Medication Details' },
  });

export const useGetPatientMedicalDataLabResultsSummaries = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientLabResultsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Lab Results' },
  });

export interface MedicalDataLabResultsDetailsParams {
  patientId?: string;
  labPanelId?: string;
  sourceType?: SourceType;
  dataRecordId?: string;
}

export const useGetPatientMedicalDataLabResultsDetails = (
  params: MedicalDataLabResultsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientLabResultsDetails.queryOptions({
      patientId: params.patientId || '',
      labPanelId: params.labPanelId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      dataRecordId: params.dataRecordId,
    }),
    enabled: !!params.patientId && !!params.labPanelId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Lab Result Details' },
  });

export const useGetPatientMedicalDataProceduresSummaries = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientProceduresSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Procedures' },
  });

export interface MedicalDataProceduresDetailsParams {
  patientId?: string;
  sourceType?: SourceType;
  dataRecordId?: string;
}

export const useGetPatientMedicalDataProceduresDetails = (
  params: MedicalDataProceduresDetailsParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientProceduresDetails.queryOptions({
      patientId: params.patientId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      dataRecordId: params.dataRecordId,
    }),
    enabled: !!params.patientId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Procedure Details' },
  });

export const useGetPatientMedicalDataAllergiesSummaries = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientAllergiesSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Allergies' },
  });

export interface MedicalDataAllergiesDetailsParams {
  patientId?: string;
  sourceType?: SourceType;
  dataRecordId?: string;
}

export const useGetPatientMedicalDataAllergiesDetails = (
  params: MedicalDataAllergiesDetailsParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientAllergiesDetails.queryOptions({
      patientId: params.patientId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      dataRecordId: params.dataRecordId,
    }),
    enabled: !!params.patientId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Allergy Details' },
  });

export const useGetPatientMedicalDataVaccinationsSummaries = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientVaccinationsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Vaccinations' },
  });

export interface MedicalDataVaccinationsDetailsParams {
  patientId?: string;
  sourceType?: SourceType;
  dataRecordId?: string;
}

export const useGetPatientMedicalDataVaccinationsDetails = (
  params: MedicalDataVaccinationsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientVaccinationsDetails.queryOptions({
      patientId: params.patientId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      dataRecordId: params.dataRecordId,
    }),
    enabled: !!params.patientId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Vaccination Details' },
  });

export const useGetPatientMedicalDataVitals = (
  params?: MedicalDataQueryParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientVitalsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Vitals' },
  });

export interface MedicalDataVitalsDetailsParams {
  patientId?: string;
  vitalId?: string;
  sourceType?: SourceType;
  dataRecordId?: string;
}

export const useGetPatientMedicalDataVitalsDetails = (
  params: MedicalDataVitalsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalDataRouter.getPatientVitalsDetails.queryOptions({
      patientId: params.patientId || '',
      vitalId: params.vitalId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      dataRecordId: params.dataRecordId,
    }),
    enabled: !!params.patientId && !!params.vitalId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Vital Details' },
  });
